import {
    type EmploymentStatus,
    type InsuranceCoverage,
    type InvestmentType,
    type SubscriptionProduct,
    type TaxFiling,
} from '@fruitful/types';

import type { KYCVerificationStatus } from '../../banking/types/BankAccount';
import type { Guide } from '../../guidance/types/Guide';
import type { UserNotificationChannel } from '../../settings/types/UserNotificationChannel';

// keep in sync with apps/backend/src/modules/user/UserService.ts
export enum UserType {
    PRIMARY_MEMBER = 'PRIMARY_MEMBER',
    PARTNER_MEMBER = 'PARTNER_MEMBER',
    JOINT_ACCOUNT_HOLDER = 'JOINT_ACCOUNT_HOLDER',
    PROSPECT = 'PROSPECT',
}

export interface User {
    createdAt: string;
    dateOfBirth: string | null;
    email: string;
    firstName: string | null;
    lastName: string | null;
    legalFirstName?: string;
    legalLastName?: string;
    hasMaxBankAccounts: boolean | null;
    id: string;
    isEmailVerified: boolean;
    isPhoneOnStytch: boolean;
    isFullyAuthed: boolean;
    isUsingBiometrics: boolean;
    isSubscriptionActive: boolean | null;
    phoneNumber: string | null;
    stytchId: string;
    stripeCustomerId?: string;
    syncteraKycStatus: KYCVerificationStatus | null;
    syncteraPersonId: string;
    updatedAt: string;
    userAddress: UserAddress | null;
    onboardingStatus: OnboardingStatus;
    membership?: UserMembership | null;
    disableMfa?: boolean | null;
    isPartial?: boolean;
    partnerMember?: Partial<User>;
    userNotificationChannels?: UserNotificationChannel[];
    externalAccountLinkingEnabled?: boolean;
    type: UserType;
    depositoryAccountLimit: number;
    needsEmploymentUpdate?: boolean;
    isRoadmapActive?: boolean;
    pronouns: string | null;
    taxFiling: TaxFiling | null;
    employmentStatus: EmploymentStatus | null;
    insuranceCoverage: InsuranceCoverage[];
    category: string; // UserCategory enum on the backend
    hasPlusFeatures?: boolean | null;
    spinwheelId: string;
    spinwheelOverride?: boolean | null;
    memberJourneyLog?: MemberJourneyLog;
    apexLegalNaturalPersonId?: string;
    referralCode?: string;
    currentYearReferralCount?: number;
    visitorId?: string;
}

export interface UserMembership {
    id: string;
    guideId?: string;
    guide?: Guide | null;
    createdAt: string;
    stripeProductName?: string | null;
    hasPlusFeatures?: boolean;
    product?: SubscriptionProduct | null;
}

export interface MemberJourneyLog {
    completedSignupMoneyMapChat?: boolean;
    completedGatedOnboarding?: boolean;
    viewedLinkMoreAccounts?: boolean;
    bookedDiscoverySession?: boolean;
    completedDiscoverySession?: boolean;
    bookedRoadmapSession?: boolean;
    completedRoadmapSession?: boolean;
    completedMoneyMap?: boolean;
    mentionedPartnerInOnboardingChat?: boolean;
    /** Deprecated - feature no longer exists */
    completedWelcomeVideo?: boolean;
}

export enum AddressType {
    LEGAL = 'LEGAL',
    BILLING = 'BILLING',
    SHIPPING = 'SHIPPING',
    OTHER = 'OTHER',
}

export interface UserAddress {
    id: string;
    type: AddressType;
    line1: string;
    line2?: string | null;
    city: string;
    postalCode: string;
    state?: string | null;
    countryCode: string;
}

export enum OnboardingStatus {
    GUIDE_SELECTION = 'GUIDE_SELECTION',
    MEMBERSHIP = 'MEMBERSHIP',
    CHECKOUT = 'CHECKOUT',
    CONTACT_INFO = 'CONTACT_INFO',
    WELCOME_CALL = 'WELCOME_CALL',
    COMPLETED = 'COMPLETED',
    JOINT_KYC_CONTACT_INFO = 'JOINT_KYC_CONTACT_INFO',
    JOINT_COMPLETED = 'JOINT_COMPLETED',
}

export interface MemberInfo {
    firstName?: string;
}

export interface UpdateUserBody {
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    pronouns?: string | null;
    employmentStatus?: EmploymentStatus;
    taxFiling?: TaxFiling;
    insuranceCoverage?: InsuranceCoverage[];
    investments?: InvestmentType[];
}

export interface UpsertUserAddressBody {
    id?: string;
    userId: string;
    type: AddressType;
    line1: string;
    line2?: string;
    city: string;
    postalCode: string;
    state?: string;
    countryCode: string;
}
